import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

/*************************************************************************
 * DraggableContainer Component
 * 
 * A draggable container that can be moved around the viewport.
 * It includes a header for dragging and an optional collapse functionality.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.title - The content to display in the header of the container.
 * @param {React.ReactNode} props.children - The content to render inside the container.
 * @param {Object} props.initialPosition - The initial top and left position of the container.
 * @param {string} props.initialWidth - The initial width of the container.
 * @param {string} props.initialHeight - The initial height of the container.
 * @param {boolean} props.attachToViewport - If true, the container is fixed to the viewport.
 * @param {Function} props.onClose - Callback function to handle the close event.
 */
const DraggableContainer = ({
  title,
  children,
  initialPosition = { top: 100, left: 100 },
  initialWidth = "600px",
  initialHeight = "400px",
  attachToViewport = false,
  onClose = null
}) => {
  // State to track the current position of the container
  const [position, setPosition] = useState(initialPosition);

  // State to track if the container is collapsed
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Ref to the draggable container element
  const draggableRef = useRef(null);

  // Refs to store previous dimensions before collapse
  const prevHeightRef = useRef(initialHeight);
  const prevWidthRef = useRef(initialWidth);

  // Ref to track if the user is currently dragging the container
  const isDraggingRef = useRef(false);

  // Ref to store the offset position when dragging starts
  const offsetRef = useRef({ x: 0, y: 0 });

  // Ref to store the last known position
  const lastPosition = useRef(position);

  // Ref for animation frame requests (if needed for smooth updates)
  const animationFrame = useRef(null);

/*************************************************************************
 * useEffect: Manage Collapse and Expand Behavior
 *
 * - When `isCollapsed` is `true`:
 *     - Sets the container's height to match the header's height, effectively hiding the content.
 * - When `isCollapsed` is `false`:
 *     - Restores the container's height and width to their previous values before collapse.
 */
  useEffect(() => {
    const adjustDimensions = () => {
        if (isCollapsed) {
        const headerHeight = draggableRef.current.querySelector('.draggable-header').offsetHeight;
        draggableRef.current.style.height = isCollapsed ? `${headerHeight}px` : '';
        }
        else{
          draggableRef.current.style.height = prevHeightRef.current;
          draggableRef.current.style.width = prevWidthRef.current;
        }
    };
    adjustDimensions();
  }, [isCollapsed]);

  /*************************************************************************
   * Handler for initiating the drag on mouse down
   * @param {MouseEvent} e - The mouse event.
   */
  const handleMouseDown = (e) => {
    // Only respond to left-click
    if (e.button !== 0) return;
    const draggable = draggableRef.current;
    if (!draggable) return;

    isDraggingRef.current = true;
    // Calculate the offset between mouse position and container's top-left corner
    offsetRef.current = {
      x: e.clientX - draggable.offsetLeft,
      y: e.clientY - draggable.offsetTop,
    };

    // Add event listeners for mouse move and mouse up to handle dragging
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

/*************************************************************************
 * Handler for updating the container's position during drag
 * @param {MouseEvent} e - The mouse event.
 */
  const handleMouseMove = (e) => {
    // If not currently dragging or no mouse buttons are pressed, terminate dragging
    if (!isDraggingRef.current || e.buttons === 0) {
      handleMouseUp();
      return;
    }

    // Calculate the new top and left positions based on mouse movement and initial offset
    const newTop = e.clientY - offsetRef.current.y;
    const newLeft = e.clientX - offsetRef.current.x;

    // Retrieve the current viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Define the margin to keep the container within the viewport
    const MARGIN = 0;

    // Calculate the updated position while keeping the container within viewport bounds
    const updatedPosition = {
      top: Math.max(MARGIN, Math.min(newTop, viewportHeight - draggableRef.current.offsetHeight - MARGIN)),
      left: Math.max(MARGIN, Math.min(newLeft, viewportWidth - draggableRef.current.offsetWidth - MARGIN)),
    };

    // Update the last known position with the newly calculated position
    lastPosition.current = updatedPosition;

    // If no animation frame is currently scheduled, request one to update the state
    if (!animationFrame.current) {
      animationFrame.current = requestAnimationFrame(() => {
        // Update the position state to re-render the container at the new position
        setPosition(lastPosition.current);

        // Reset the animationFrame reference to allow future updates
        animationFrame.current = null;
      });
    }
  };

/*************************************************************************
 * Handler for ending the drag action when the mouse button is released.
 */
  const handleMouseUp = () => {
    // Set the dragging state to false to indicate that dragging has ended
    isDraggingRef.current = false;

    // Remove the mousemove and mouseup event listeners as dragging is no longer active
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);

  // If an animation frame is pending, cancel it to prevent unnecessary updates
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
      animationFrame.current = null;
    }
  };

  /*************************************************************************
  * Toggles the collapsed state of the draggable container.
  *
  * - When `isCollapsed` is `false`:
  *     - Stores the current height and width of the container.
  *     - Sets isCollapsed to `true` to collapse the container.
  * - When `isCollapsed` is `true`:
  *     - Sets isCollapsed to `false` to expand the container.
  */
  const toggleCollapse = () => {
    if (!isCollapsed && draggableRef.current) {
      // Store current dimensions before collapsing
      const computedStyle = window.getComputedStyle(draggableRef.current);
      prevHeightRef.current = computedStyle.height;
      prevWidthRef.current = computedStyle.width;
    }
    setIsCollapsed(!isCollapsed);
  }

  /*************************************************************************
   * JSX structure for the draggable container content.
   * It encapsulates the header and the collapsible content area.
   */
  const containerContent = (
    <div
      ref={draggableRef}
      className="draggable shadow-lg border border-gray-300 rounded-lg bg-white"
      style={{
        position: attachToViewport ? "fixed" : "absolute", // Position fixed relative to viewport or absolute within parent
        top: position.top, // Current top position from state - default through prop
        left: position.left, // Current left position from state - default through prop
        width: isCollapsed ? "auto" : initialWidth, // Use initialWidth prop
        height: isCollapsed ? "auto" : initialHeight, // Use initialHeight prop
        minWidth: isCollapsed ? "auto" : "300", // Minimum width when expanded to ensure usability
        maxWidth: "100vw", // Maximum width relative to viewport width to prevent overflow
        maxHeight: "100vh", // Maximum height relative to viewport height to prevent overflow
        resize: isCollapsed ? "none" : "both", // Allow resizing only when expanded
        overflow: isCollapsed ? "hidden" : "auto", // Hide overflow content when collapsed
        zIndex: attachToViewport ? 9999 : 1000, // Higher z-index when attached to viewport to overlay other elements
      }}
    >
      {/* Header Section */}
      <div
        className="draggable-header cursor-move p-2 bg-gray-700 text-white rounded-t-lg flex justify-between items-center"
        onMouseDown={handleMouseDown} // Initiate dragging on mouse down
      >
        <span>{title}</span>
        <div className="flex items-center space-x-3 pl-3">
          <button
            onClick={(e) => {
              e.stopPropagation(); // Prevents the click from bubbling up to the draggable header
              toggleCollapse(); // Toggle the collapsed state to show/hide content
            }}
            className="w-6 h-6 flex justify-center items-center text-white bg-gray-500 hover:bg-gray-600 rounded"
          >
            {isCollapsed ? "+" : "-"} {/* Display "+" when collapsed and "-" when expanded */}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="w-6 h-6 flex justify-center items-center text-white bg-gray-500 hover:bg-gray-600 rounded"
            >
              &times;
            </button>
          )}
        </div>
      </div>

      {/* Content Section */}
      {!isCollapsed && (
        <div className="p-4">
          {children} {/* Render the child components or content passed to DraggableContainer */}
        </div>
      )}
    </div>
  );

  return attachToViewport 
    ? createPortal(containerContent, document.body) // Render containerContent as a portal attached to document.body
    : containerContent; // Otherwise, render containerContent inline within the parent component

};

export default DraggableContainer;